// ---------------------------------------------------------------------------
// Checkbox and Switch, ported from the design system (R8)
//
// <sdr-checkbox>: an on/off choice that takes effect when a form is saved, and
// the row selector in tables; `indeterminate` shows a partial selection.
// <sdr-switch>: a change that applies at once.
//
// Both are light-DOM Lit elements because they hold state. As in the system,
// `checked` makes one controlled — it shows `checked` and reports a change
// through onChange without flipping itself; without `checked` it starts at
// `defaultChecked` and keeps its own state. The system's className is
// checkClass and switchClass here, so the element keeps its own class.
//
// checkbox() is the same checkbox as a template, always controlled, for a
// component that holds the state itself (the DataTable's row selectors, the
// LaunchChecklist's confirmation): its markup has no element around it, as in
// the system. One line ahead of the system (R9): a click clears the browser's
// indeterminate flag, and a caller that draws the box partial again gets it
// back (live); the system's effect does not run again and loses it.
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';

import { clickOnDisabled, cx, noBox, node, present } from './helpers.js';
import { icon } from './icons.js';

/** The system's useControlled: a value given from outside wins, otherwise the element keeps its own. */
abstract class Toggle extends LitElement {
  static override properties = {
    checked: { attribute: false },
    defaultChecked: { attribute: false },
    disabled: { type: Boolean },
    label: { attribute: false },
    description: { attribute: false },
    ariaLabelText: { attribute: false },
    onChange: { attribute: false },
    own: { state: true },
  };

  declare checked: boolean | undefined;
  declare defaultChecked: boolean | undefined;
  declare disabled: boolean;
  declare label: unknown;
  declare description: unknown;
  /** The accessible name when there is no visible label (the system's aria-label). */
  declare ariaLabelText: string | undefined;
  declare onChange: ((checked: boolean) => void) | undefined;
  protected declare own: boolean | undefined;

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
  }

  protected override willUpdate(_changed: PropertyValues<this>) {
    if (this.own === undefined) this.own = !!this.defaultChecked;
  }

  get on(): boolean {
    return this.checked !== undefined ? this.checked : !!this.own;
  }

  protected set(next: boolean) {
    if (this.checked === undefined) this.own = next;
    else this.requestUpdate(); // controlled: draw the given value again, whatever the control did
    this.onChange?.(next);
  }
}

export interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  label?: unknown;
  description?: unknown;
  /** The accessible name when there is no visible label (the system's aria-label). */
  ariaLabel?: string;
  className?: string;
  onChange?: (checked: boolean) => void;
}

/** The system's Checkbox as a template, controlled: the caller draws it again with the new value. */
export function checkbox(p: CheckboxProps): TemplateResult {
  const box = html`<span class="sdr-check__box"
    ><input
      type="checkbox"
      class="sdr-check__input"
      ?checked=${p.checked}
      .checked=${live(p.checked)}
      .indeterminate=${live(!!p.indeterminate)}
      ?disabled=${p.disabled}
      aria-label=${ifDefined(present(p.label) ? undefined : p.ariaLabel)}
      @change=${(e: Event) => p.onChange?.((e.target as HTMLInputElement).checked)}
    />${icon({ name: 'check', size: 16, className: 'sdr-check__mark' })}<span
      class="sdr-check__dash"
      aria-hidden="true"
    ></span
  ></span>`;
  if (!present(p.label)) return html`<span class=${cx('sdr-check', 'sdr-check--bare', p.className)}>${box}</span>`;
  return html`<label class=${cx('sdr-check', p.disabled && 'is-disabled', p.className)}
    >${box}<span class="sdr-check__text"
      ><span class="sdr-check__label">${node(p.label)}</span>${
        present(p.description) ? html`<span class="sdr-check__desc">${node(p.description)}</span>` : nothing
      }</span
    ></label
  >`;
}

export class SdrCheckbox extends Toggle {
  static override properties = {
    ...Toggle.properties,
    indeterminate: { type: Boolean },
    checkClass: { attribute: false },
  };

  declare indeterminate: boolean;
  declare checkClass: string | undefined;

  override render() {
    return checkbox({
      checked: this.on,
      indeterminate: this.indeterminate,
      disabled: this.disabled,
      label: this.label,
      description: this.description,
      ariaLabel: this.ariaLabelText,
      className: this.checkClass,
      onChange: next => this.set(next),
    });
  }
}

let switches = 0;

export class SdrSwitch extends Toggle {
  static override properties = { ...Toggle.properties, switchClass: { attribute: false } };

  declare switchClass: string | undefined;
  private readonly controlId = `sdr-switch-${++switches}`;

  override render() {
    const on = this.on;
    const id = this.controlId;
    return html`<div class=${cx('sdr-switch', this.disabled && 'is-disabled', this.switchClass)}>
      <button
        type="button"
        role="switch"
        id=${id}
        aria-checked=${String(on)}
        ?disabled=${this.disabled}
        class="sdr-switch__btn"
        aria-label=${ifDefined(present(this.label) ? undefined : this.ariaLabelText)}
        aria-labelledby=${ifDefined(present(this.label) ? `${id}-l` : undefined)}
        @click=${(e: MouseEvent) => {
          // A click on the knob of a disabled switch flips nothing, as React never calls onClick there
          if (!clickOnDisabled(e)) this.set(!on);
        }}
      ><span class="sdr-switch__knob"></span></button>
      ${
        present(this.label)
          ? html`<div class="sdr-switch__text">
            <label id=${`${id}-l`} for=${id} class="sdr-switch__label">${node(this.label)}</label>
            ${present(this.description) ? html`<div class="sdr-switch__desc">${node(this.description)}</div>` : nothing}
          </div>`
          : nothing
      }
    </div>`;
  }
}

if (!customElements.get('sdr-checkbox')) customElements.define('sdr-checkbox', SdrCheckbox);
if (!customElements.get('sdr-switch')) customElements.define('sdr-switch', SdrSwitch);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-checkbox': SdrCheckbox;
    'sdr-switch': SdrSwitch;
  }
}
